import { Button, cn } from '@kalos/ui';
import { type FC, type ReactNode } from 'react';

interface WorkflowStep {
  id: string;
  title: string;
  shortTitle?: string; // Add optional short title for mobile
  content: ReactNode;
}

interface ProposalWorkflowProps {
  currentStep: 'services' | 'review' | 'settings';
  servicesContent: ReactNode;
  reviewContent: ReactNode;
  settingsContent: ReactNode;
  onNext: () => void;
  onBack: () => void;
  customActionButton?: ReactNode; // Custom action button for specific steps
  disableContinue?: boolean; // New prop to disable the continue button
}

export const ProposalWorkflow: FC<ProposalWorkflowProps> = ({
  currentStep,
  servicesContent,
  reviewContent,
  settingsContent,
  onNext,
  onBack,
  customActionButton,
  disableContinue = false,
}) => {
  const steps: WorkflowStep[] = [
    {
      id: 'services',
      title: 'Services',
      shortTitle: 'Services',
      content: servicesContent,
    },
    {
      id: 'review',
      title: 'Review & Documentation',
      shortTitle: 'Review',
      content: reviewContent,
    },
    {
      id: 'settings',
      title: 'Review & Share',
      shortTitle: 'Share',
      content: settingsContent,
    },
  ];

  // Determine step index for progress indicator
  const currentStepIndex = steps.findIndex((step) => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  return (
    <div className="relative mx-auto flex min-h-[calc(100vh-200px)] w-full max-w-[1200px] flex-col bg-white px-2 sm:px-6">
      {/* Progress Indicator */}
      <div className="flex border-b bg-white">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={cn(
              '-mb-px flex-1 border-b-2 px-1 py-3 text-center text-xs font-medium transition-colors sm:px-6 sm:text-sm',
              step.id === currentStep
                ? 'border-primary text-primary'
                : index < currentStepIndex
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500',
            )}
          >
            <div className="flex flex-col items-center justify-center gap-1 sm:flex-row sm:gap-2">
              <span
                className={cn(
                  'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs',
                  step.id === currentStep
                    ? 'bg-accent text-accent-foreground'
                    : index < currentStepIndex
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-600',
                )}
              >
                {index + 1}
              </span>
              {/* Show short title on mobile, full title on larger screens */}
              <span className="hidden sm:inline">{step.title}</span>
              <span className="inline sm:hidden">{step.shortTitle || step.title}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Content Area - Flex grow to push navigation to bottom */}
      <div className="mt-4 flex-grow overflow-y-auto bg-white pb-20">
        {steps.find((step) => step.id === currentStep)?.content}
      </div>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="sticky bottom-0 left-0 right-0 mt-4 flex justify-between border-t bg-white px-2 py-4 sm:px-4">
        <Button variant="outline" onClick={onBack} disabled={isFirstStep}>
          Back
        </Button>

        {!isLastStep && (
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90"
            disabled={disableContinue}
          >
            Continue
          </Button>
        )}

        {/* Show custom action button if provided for the last step */}
        {isLastStep && customActionButton}
      </div>
    </div>
  );
};
